/**
 * Light, dark, or whatever the system says.
 *
 * A preference about this screen, not about the data, so it lives in this
 * browser's local storage rather than in the database: it is not backed up,
 * not restored, and two devices can disagree about it. `tokens.css` does the
 * rest from `data-theme` on the root element; with no attribute the system
 * setting decides.
 *
 * Every storage access is wrapped. Private windows and blocked site data throw
 * on access, and a failure to remember a colour scheme must never stop the app
 * from starting.
 */

export type ThemeChoice = 'system' | 'light' | 'dark'

export const THEME_CHOICES: readonly ThemeChoice[] = ['system', 'light', 'dark']

const KEY = 'anytracker.theme'

function isChoice(value: unknown): value is ThemeChoice {
  return value === 'system' || value === 'light' || value === 'dark'
}

export function readTheme(): ThemeChoice {
  try {
    const stored = window.localStorage.getItem(KEY)
    return isChoice(stored) ? stored : 'system'
  } catch {
    return 'system'
  }
}

export function applyTheme(choice: ThemeChoice): void {
  const root = document.documentElement
  if (choice === 'system') {
    delete root.dataset['theme']
  } else {
    root.dataset['theme'] = choice
  }
}

export function writeTheme(choice: ThemeChoice): void {
  applyTheme(choice)
  try {
    if (choice === 'system') {
      window.localStorage.removeItem(KEY)
    } else {
      window.localStorage.setItem(KEY, choice)
    }
  } catch {
    // Applied for this session even if it cannot be remembered.
  }
}

/** Called once at startup, before the first render, so nothing flashes. */
export function applyStoredTheme(): void {
  applyTheme(readTheme())
}
